import { Search } from 'js-search'
import AdminContext from 'lib/context/AdminContext'
import ProductCategory from 'pages/Home/ProductCategory'
import RecordedWebinarType from 'pages/Home/RecordedWebinarType'
import { filterSeries, filterUpcomingWebinars } from 'pages/Home/sections/filtering'
import LoadingList from 'pages/Home/sections/LoadingList'
import Section from 'pages/Home/sections/Section'
import Series from 'pages/Home/Series'
import UpcomingWebinarType from 'pages/Home/UpcomingWebinarType'
import React, { useContext, useMemo } from 'react'
import { Helmet } from 'react-helmet'
import { useTranslation } from 'react-i18next'

import getTranslationKeyFromCategory from '../getTranslationKeyFromCategory'
import useSearchQuery from '../search/useSearchQuery'
import useSearchResults from '../search/useSearchResults'
import SectionZeroState from '../sections/SectionZeroState'
import SkillLevel from '../SkillLevel'
import StartTime from '../StartTime'
import UpcomingWebinarsHeader from './UpcomingWebinarsHeader'
import UpcomingWebinarsList from './UpcomingWebinarsList'

export interface UpcomingWebinarsSectionProps {
  currentProductCategory: ProductCategory
  loading: boolean
  selectedSeries?: Series
  series: Series[]
  selectedSkillLevel: SkillLevel
  selectedStartTime: StartTime
  recordedWebinars: RecordedWebinarType[]
  upcomingWebinars: UpcomingWebinarType[]
  upcomingWebinarsSearch?: Search
}

export default function UpcomingWebinarsSection(props: UpcomingWebinarsSectionProps) {
  const {
    currentProductCategory,
    loading,
    recordedWebinars,
    selectedSeries,
    selectedSkillLevel,
    selectedStartTime,
    series: seriesList,
    upcomingWebinars,
    upcomingWebinarsSearch
  } = props
  const { hidePastWebinars, showAdminControls } = useContext(AdminContext)
  const [t] = useTranslation()
  const filteredSeries = useMemo(() => filterSeries(seriesList, showAdminControls), [seriesList, showAdminControls])
  const searchResults = useSearchResults(upcomingWebinarsSearch)
  const search = useSearchQuery()

  const filteredWebinars =
    searchResults ||
    filterUpcomingWebinars(
      upcomingWebinars,
      currentProductCategory,
      selectedSeries,
      selectedSkillLevel,
      selectedStartTime,
      showAdminControls,
      hidePastWebinars
    )

  function renderHelmet() {
    const key = getTranslationKeyFromCategory(currentProductCategory)

    return (
      <Helmet defer={false}>
        <title>{t(`home.upcomingWebinarsSection.helmet.${key}.title`)}</title>
        <meta name="description" content={t(`home.upcomingWebinarsSection.helmet.${key}.description`)} />
        <meta name="keywords" content={t(`home.helmet.${key}.keywords`)} />
      </Helmet>
    )
  }

  function renderList() {
    if (loading) {
      return <LoadingList />
    }
    if (filteredWebinars.length === 0 && !search) {
      return (
        <SectionZeroState>
          <SectionZeroState.Message>{t('home.upcomingWebinarsSection.zeroState.message')}</SectionZeroState.Message>
          &nbsp;
          <SectionZeroState.ClearFiltersLink />
        </SectionZeroState>
      )
    }

    return (
      <UpcomingWebinarsList
        recordedWebinars={recordedWebinars}
        renderSeriesLink={!selectedSeries || !!searchResults}
        upcomingWebinars={filteredWebinars}
      />
    )
  }

  return (
    <Section>
      {renderHelmet()}
      <UpcomingWebinarsHeader
        currentProductCategory={currentProductCategory}
        loading={loading}
        searchResults={searchResults}
        selectedSkillLevel={selectedSkillLevel}
        selectedSeries={selectedSeries}
        selectedStartTime={selectedStartTime}
        series={filteredSeries}
        recordedWebinars={recordedWebinars}
      />
      {renderList()}
    </Section>
  )
}
